import {
  CREDIT_COPY,
  type CreditEntryDto,
  type CreditLogPage,
  dollars,
  TEXTS_COMING_SOON,
  textsLeft,
} from '@eventping/shared'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { CircleCheck, Download, ExternalLink, ReceiptText } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useCredit } from '@/components/credit'
import { Card, Footer, Page } from '@/components/shell'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { api, useMe } from '@/lib/api'
import { cn } from '@/lib/utils'
import { CreatorHeader } from './home'

/**
 * The text log (/account/texts, docs/PRICING.md §2.9): every text charged and every credit
 * added, newest first, with the running balance after each. Filter by event and by month;
 * export the current filter as CSV. Masked phone numbers only, no message bodies.
 */
export type TextLogSearch = { event?: number; month?: string; purchase?: string }

const select =
  'h-11 w-full rounded-xl border border-input bg-card px-3 text-base outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50'

export function TextLogPage({ search }: { search: TextLogSearch }) {
  const me = useMe()
  const navigate = useNavigate()
  const client = useQueryClient()
  // Texts-off mode: nothing to log, and nothing is fetched.
  const textsOn = Boolean(me.data?.user && me.data.textsEnabled)
  const credit = useCredit(textsOn)
  const tz = new Date().getTimezoneOffset()
  const params = useMemo(() => {
    const p = new URLSearchParams()
    if (search.event) p.set('event', String(search.event))
    if (search.month) p.set('month', search.month)
    p.set('tz', String(tz))
    return p
  }, [search.event, search.month, tz])
  const log = useInfiniteQuery({
    queryKey: ['texts', search.event ?? null, search.month ?? null],
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(params)
      if (pageParam) p.set('before', pageParam)
      return api<CreditLogPage>(`/api/account/texts?${p}`)
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: textsOn,
  })

  // Back from Stripe Checkout: the webhook adds the texts a moment later, so look again.
  const purchased = search.purchase === 'success'
  useEffect(() => {
    if (!purchased) return
    const timer = setInterval(() => {
      void client.invalidateQueries({ queryKey: ['credit'] })
      void client.invalidateQueries({ queryKey: ['texts'] })
    }, 2000)
    const stop = setTimeout(() => clearInterval(timer), 30_000)
    return () => {
      clearInterval(timer)
      clearTimeout(stop)
    }
  }, [purchased, client])

  if (me.data && !me.data.user) {
    return (
      <Page>
        <p>
          Please{' '}
          <Link to="/" className="underline">
            sign in
          </Link>{' '}
          first.
        </p>
      </Page>
    )
  }
  if (!me.data) return <Page>{null}</Page>
  if (!me.data.textsEnabled) {
    return (
      <Page header={<CreatorHeader me={me.data} />}>
        <Card className="text-center" data-testid="texts-coming-soon">
          <h1 className="text-xl font-semibold">{TEXTS_COMING_SOON}</h1>
          <p className="mt-2 text-muted-foreground">
            Updates go out by email for now, and email is free.
          </p>
          <p className="mt-4">
            <Link to="/" className="underline">
              Back to your events
            </Link>
          </p>
        </Card>
        <Footer />
      </Page>
    )
  }

  const entries = log.data?.pages.flatMap((p) => p.entries) ?? []
  const events = log.data?.pages[0]?.events ?? []
  const filtered = Boolean(search.event || search.month)
  const setSearch = (next: Partial<TextLogSearch>) =>
    void navigate({
      to: '/account/texts',
      search: { event: search.event, month: search.month, ...next, purchase: undefined },
    })

  return (
    <Page header={<CreatorHeader me={me.data} />}>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold">Text log</h1>
        <p className="text-sm text-muted-foreground">
          Every text charged to your account and every credit added. Email updates are free and
          aren’t listed.
        </p>
      </div>

      {purchased && (
        <div
          role="status"
          data-testid="purchase-thanks"
          className="mb-4 flex gap-3 rounded-2xl border border-done/40 bg-done-soft p-4 text-sm"
        >
          <CircleCheck className="mt-0.5 size-4 shrink-0 text-done" aria-hidden />
          <p>
            <strong className="font-semibold">Thanks for your purchase.</strong> Your texts show up
            here as soon as the payment is confirmed, usually within a few seconds.
          </p>
        </div>
      )}

      <Card className="mb-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm text-muted-foreground">Balance</p>
          <p className="text-3xl font-semibold" data-testid="log-balance">
            {credit.data ? textsLeft(credit.data.balance) : '…'}
          </p>
          {credit.data?.state === 'zero' && (
            <p className="mt-1 text-sm text-destructive">{CREDIT_COPY.zero}</p>
          )}
          {credit.data?.state === 'low' && (
            <p className="mt-1 text-sm text-waiting">{CREDIT_COPY.low}</p>
          )}
        </div>
        <Button asChild>
          <Link to="/pricing">Buy texts</Link>
        </Button>
      </Card>

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor="log-event">Event</Label>
            <select
              id="log-event"
              className={select}
              value={search.event ?? ''}
              onChange={(e) =>
                setSearch({ event: e.target.value ? Number(e.target.value) : undefined })
              }
            >
              <option value="">All events</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="log-month">Month</Label>
            <input
              id="log-month"
              type="month"
              className={select}
              value={search.month ?? ''}
              onChange={(e) => setSearch({ month: e.target.value || undefined })}
            />
          </div>
          <Button asChild variant="outline">
            <a href={`/api/account/texts.csv?${params}`} download data-testid="csv-export">
              <Download /> Export CSV
            </a>
          </Button>
        </div>
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setSearch({ event: undefined, month: undefined })}
          >
            Clear filters
          </Button>
        )}
      </Card>

      <Card className="p-0">
        {log.isPending ? (
          <p className="p-5 text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="p-5 text-muted-foreground" data-testid="log-empty">
            {filtered ? 'Nothing matches these filters.' : CREDIT_COPY.logEmpty}
          </p>
        ) : (
          <table className="w-full text-sm" data-testid="text-log">
            <thead className="border-b text-left text-muted-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium">
                  Entry
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  Texts
                </th>
                <th scope="col" className="px-4 py-3 text-right font-medium">
                  Balance
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map((e) => (
                <Row key={e.id} e={e} />
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {log.hasNextPage && (
        <Button
          variant="outline"
          className="mt-4 w-full"
          disabled={log.isFetchingNextPage}
          onClick={() => void log.fetchNextPage()}
        >
          Show older entries
        </Button>
      )}
      <Footer />
    </Page>
  )
}

function when(ms: number): string {
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function Row({ e }: { e: CreditEntryDto }) {
  const { title, detail } = describe(e)
  return (
    <tr id={`entry-${e.id}`} data-testid={`entry-${e.kind}`} className="align-top">
      <td className="px-4 py-3">
        <p className="font-medium break-words">{title}</p>
        {detail && <p className="text-muted-foreground break-words">{detail}</p>}
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          <time dateTime={new Date(e.createdAt).toISOString()}>{when(e.createdAt)}</time>
          {e.kind === 'debit' && (
            <span
              className={cn(
                'rounded-full px-2 py-0.5',
                e.status === 'credited_back'
                  ? 'bg-done-soft text-done'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {e.status === 'credited_back' ? 'Credited back' : 'Sent'}
            </span>
          )}
          {e.receiptUrl && (
            <a
              href={e.receiptUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-medium text-foreground underline underline-offset-4"
            >
              <ReceiptText className="size-3.5" aria-hidden /> Receipt
              <ExternalLink className="size-3" aria-hidden />
            </a>
          )}
          {e.kind === 'credit_back' && e.reversesId && (
            <a href={`#entry-${e.reversesId}`} className="underline underline-offset-4">
              The text it reverses
            </a>
          )}
        </p>
      </td>
      <td
        className={cn(
          'px-4 py-3 text-right font-medium whitespace-nowrap tabular-nums',
          e.texts > 0 ? 'text-done' : 'text-foreground',
        )}
      >
        {e.texts > 0 ? `+${e.texts.toLocaleString('en-US')}` : `−${Math.abs(e.texts)}`}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap tabular-nums">
        {e.balanceAfter.toLocaleString('en-US')}
      </td>
    </tr>
  )
}

function describe(e: CreditEntryDto): { title: string; detail: string | null } {
  const who = e.recipient
    ? [e.recipient.name, e.recipient.phone?.replace(' ', '\u00a0')].filter(Boolean).join(' · ')
    : null
  const where = [e.event?.title, who].filter(Boolean).join(' · ') || null
  switch (e.kind) {
    case 'debit':
      return {
        title: e.messageKind === 'step' ? `Step: ${e.stepName ?? 'update'}` : 'Confirmation',
        detail: where,
      }
    case 'credit_back':
      return { title: '+1, failed text credited back', detail: where }
    case 'welcome':
      return { title: `Welcome credit +${e.texts}`, detail: 'Your first $1 is on us.' }
    case 'purchase':
      return {
        title: `+${e.texts.toLocaleString('en-US')} texts${e.amountCents != null ? `, ${dollars(e.amountCents)}` : ''}`,
        detail: 'Purchase',
      }
    case 'chargeback':
      return {
        title: `${e.texts} texts, card dispute`,
        detail: e.note ?? 'A card dispute was decided against this purchase.',
      }
    case 'adjustment':
      return {
        title: `Adjustment ${e.texts > 0 ? '+' : '−'}${Math.abs(e.texts)}`,
        detail: e.note,
      }
  }
}
